import { useEffect } from 'react'
import { selectSessionId, selectStoreId } from '../features/app/selectors'
import { selectUsername } from '../features/auth/selectors'
import { selectPanelStoresIds } from '../features/panels/selectors'
import { connectToSocketDoctorStore } from '../features/socket/doctorExamsInStore/doctorExamsInStoreSocket'
import {
	connectToRoom,
	connectToRoomNoType,
	disconnectFromRoom,
} from '../features/socket/room/roomSocketActions'
import { RoomSocketType } from '../features/socket/room/roomSocketTypes'
import { InstrumentType } from '../model/instrumentsApi'
import { Id } from '../model/model'
import { useTeloNavigate } from '../routing/teloNavigate'
import { worklistApi, WORKLIST_CACHE_TAG } from '../services/worklist'
import { useTeloDispatch, useTeloSelector } from '../store'

const useRoomSocket = (roomType: RoomSocketType, roomName?: string) => {
	const dispatch = useTeloDispatch()
	const navigate = useTeloNavigate()
	useEffect(() => {
		if (!roomName) {
			return
		}
		dispatch(connectToRoom(roomType, roomName, navigate))
	}, [dispatch, navigate, roomName, roomType])
}

export const useSocketUser = (username: string) =>
	useRoomSocket('user-id', username ? `user-id-${username}` : undefined)

export const useSocketStore = () => {
	const storeId = useTeloSelector(selectStoreId)
	return useRoomSocket('store-id', storeId ? `store-id-${storeId}` : undefined)
}

export const useSocketAssessments = (isAppInsideEclips?: boolean) =>
	useRoomSocket('assessments', isAppInsideEclips ? undefined : 'assessments')

export const useSocketExamById = (examId?: Id) =>
	useRoomSocket('exam-id', examId ? `exam-id-${examId}` : undefined)

export const useSocketInstrument = (
	examId: Id,
	examLoaded: boolean,
	autoUpdate: boolean,
	instrumentType: InstrumentType,
) =>
	useRoomSocket(
		`instrument-type-${instrumentType}`,
		examLoaded && autoUpdate
			? `instrument-type-${instrumentType}_exam-id-${examId}`
			: undefined,
	)

export const useSocketInstrumentsInRoom = (
	storeId: string,
	stageId: Id,
	roomId: Id,
) =>
	useRoomSocket(
		'instruments-in-room',
		storeId && stageId && roomId
			? `store-id-${storeId}_stage-id-${stageId}_room-id-${roomId}`
			: undefined,
	)

export const useSocketStoreExams = (storeId?: Id) =>
	useRoomSocket('exams-in-store', storeId ? `exams-in-store-id-${storeId}` : '')

export const useSocketGetWarnings = (examId?: Id) =>
	useRoomSocket('warnings', examId ? `warnings_exam-id-${examId}` : undefined)

export const useSocketExamsInPanelStores = () => {
	const dispatch = useTeloDispatch()

	const username = useTeloSelector(selectUsername)
	const sessionId = useTeloSelector(selectSessionId)

	const panelStoresIds = useTeloSelector(selectPanelStoresIds)
	const remoteStoresIdsToFetch = panelStoresIds.length ? panelStoresIds : []
	const stores = [...new Set(remoteStoresIdsToFetch)]
	// stores id are passed as a string because stores array can be recreated even if it holds the same data
	// (for instance whenever the Worklist is rerendered)
	// and this would cause the hook to be executed again even if stores didn't actually change
	const storesIdAsString = stores.sort().join(',')

	useEffect(() => {
		if (!storesIdAsString || !username) {
			return
		}

		connectToSocketDoctorStore({
			doctorUsername: username,
			updateWorklist: () => {
				// No need to clear the worklist cache if not in the worklist page
				// since when the user will go to the worklist page the cache is invalidated
				if (window.location.pathname.includes('/worklist')) {
					dispatch(worklistApi.util.invalidateTags([WORKLIST_CACHE_TAG]))
				}
			},
			sessionId,
			storeId: storesIdAsString,
		})
	}, [dispatch, username, storesIdAsString, sessionId])
}

export const useSocketFieldUpdates = (examId?: Id) =>
	useRoomSocket('field-updates', examId ? `field-updates-${examId}` : undefined)

export const useSocketExams = (examIds: string[]) => {
	const dispatch = useTeloDispatch()
	const navigate = useTeloNavigate()

	useEffect(() => {
		if (!examIds.length) {
			return
		}

		examIds.forEach((examId, index) => {
			dispatch(connectToRoomNoType(`exam-id-${examId}`, navigate))
		})

		return () => {
			examIds.forEach((examId, index) => {
				dispatch(disconnectFromRoom(`exam-id-${examId}`))
			})
		}
	}, [dispatch, examIds, navigate])
}
